import { useState, useEffect } from 'react';
import { 
  SCALE_THRESHOLDS, 
  VACANCIES_SCALE_FACTORS,
  INTERVIEWS_SCALE_FACTORS,
  DEFAULT_INTERVIEWS_PAGE_SIZE,
  DEFAULT_VACANCIES_PAGE_SIZE,
  DEFAULT_TEAM_PAGE_SIZE,
  DEFAULT_COMPANIES_PAGE_SIZE,
  DEFAULT_ACCOUNTS_PAGE_SIZE
} from '../constants/pagination';

// Базовые размеры для FullHD (1920x1080)
const BASE_PAGE_SIZES = {
  interviews: DEFAULT_INTERVIEWS_PAGE_SIZE,
  vacancies: DEFAULT_VACANCIES_PAGE_SIZE,
  team: DEFAULT_TEAM_PAGE_SIZE,
  companies: DEFAULT_COMPANIES_PAGE_SIZE,
  accounts: DEFAULT_ACCOUNTS_PAGE_SIZE,
} as const;

// Определение коэффициента масштабирования по высоте окна браузера
const getScaleFactor = (height: number, pageType: PageSizeType): number => {
  // Выбираем коэффициенты в зависимости от типа страницы
  const scaleFactors = pageType === 'interviews' ? INTERVIEWS_SCALE_FACTORS : VACANCIES_SCALE_FACTORS;
  
  let scaleFactor: number;
  if (height >= SCALE_THRESHOLDS.LARGE) {
    scaleFactor = scaleFactors.LARGE;
  } else if (height >= SCALE_THRESHOLDS.MEDIUM) {
    // FullHD (обычно 900-1080px доступной высоты) должен оставаться без масштабирования
    // поэтому применяем DEFAULT ниже LARGE
    scaleFactor = scaleFactors.DEFAULT;
  } else if (height >= SCALE_THRESHOLDS.SMALL) {
    // Также без масштабирования для небольших высот окна
    scaleFactor = scaleFactors.DEFAULT;
  } else {
    scaleFactor = scaleFactors.DEFAULT;
  }
  
  return scaleFactor;
};

// Типы для размеров страниц
export type PageSizeType = keyof typeof BASE_PAGE_SIZES;

/**
 * Хук для получения адаптивного размера страницы в зависимости от высоты окна браузера
 * @param pageType - тип страницы (interviews, vacancies, team, companies, accounts)
 * @returns размер страницы с учетом доступной высоты окна
 */
export const useAdaptivePageSize = (pageType: PageSizeType): number => {
  const [pageSize, setPageSize] = useState<number>(() => {
    const baseSize = BASE_PAGE_SIZES[pageType];
    const scaleFactor = getScaleFactor(window.innerHeight, pageType);
    const calculatedSize = Math.round(baseSize * scaleFactor);
    return calculatedSize;
  });

  useEffect(() => {
    const handleResize = () => {
      const baseSize = BASE_PAGE_SIZES[pageType];
      const scaleFactor = getScaleFactor(window.innerHeight, pageType);
      const newPageSize = Math.round(baseSize * scaleFactor);
      setPageSize(newPageSize);
    };

    // Слушаем изменения размера окна браузера (включая DevTools)
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageType]);

  return pageSize;
};

/**
 * Получить базовый размер страницы для указанного типа (без масштабирования)
 * @param pageType - тип страницы
 * @returns базовый размер страницы для FullHD
 */
export const getBasePageSize = (pageType: PageSizeType): number => {
  return BASE_PAGE_SIZES[pageType];
};

/**
 * Получить адаптивный размер страницы для указанного типа и высоты окна
 * @param pageType - тип страницы
 * @param windowHeight - высота окна браузера (рекомендуется использовать window.innerHeight)
 * @returns размер страницы с учетом масштабирования
 */
export const getAdaptivePageSize = (pageType: PageSizeType, windowHeight: number): number => {
  const baseSize = BASE_PAGE_SIZES[pageType];
  const scaleFactor = getScaleFactor(windowHeight, pageType);
  return Math.round(baseSize * scaleFactor);
};
